<template>
  <div class="weather-effects">
    <canvas ref="canvas" class="effect-canvas"></canvas>
    <div v-if="currentWeather" class="css-effect" :class="weatherType"></div>
    <div class="weather-indicator" v-if="showIndicator && currentWeather">
      <span class="weather-icon">{{ weatherIcon }}</span>
      <span class="temp">{{ currentTemp }}°C</span>
      <span class="coins" :style="{ color: getCoinsColor }">{{ $i18n('goldCoin', '金币') }}: {{ userInfo.nowMoney }}</span>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import {_code, WS_Status} from "@utils/constant";
import i18nMixin from "@/libs/i18nMixin";
export default {
  name: 'WeatherEffects',
  mixins: [i18nMixin],
  props: {
    showIndicator: {
      type: Boolean,
      default: true
    },
    loop: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentWeather: {
        text: '晴',
        temp: '25'
      },
      currentTemp: 25,
      canvasCtx: null,
      animationFrame: null,
      particles: [],
      isCanvasReady: false,
      resizeObserver: null,
      effectStartTime: 0,
      weatherInterval: null
    }
  },

  computed: {
    ...mapState({
      wsStatus: (state) => state.app.wsStatus
    }),
    ...mapGetters(["userInfo", "live"]),
    getCoinsColor() {
      switch(this.wsStatus) {
        case WS_Status.connected: // 连通状态
          return '#FFFF00'; // 黄色
        case WS_Status.unconnected: // 未连接
          return '#FFFFFF'; // 白色
        case WS_Status.connected_err: // 错误
          return '#FF0000'; // 红色
        case WS_Status.connect_close: // 关闭
          return '#888888'; // 灰色
        default:
          return '#FFFFFF'; // 默认白色
      }
    },
    weatherIcon() {
      const iconMap = {
        '晴': '☀️',
        '多云': '⛅',
        '阴': '☁️',
        '阵雨': '🌦️',
        '雷阵雨': '⛈️',
        '雨夹雪': '🌨️',
        '小雨': '🌧️',
        '中雨': '🌧️',
        '大雨': '🌧️',
        '暴雨': '🌧️',
        '大暴雨': '🌧️',
        '特大暴雨': '🌧️',
        '阵雪': '❄️',
        '小雪': '❄️',
        '中雪': '❄️',
        '大雪': '❄️',
        '暴雪': '❄️',
        '雾': '🌫️',
        '冻雨': '🌨️',
        '沙尘暴': '🌪️',
        '浮尘': '🌫️',
        '扬沙': '🌫️',
        '强沙尘暴': '🌪️'
      }
      return iconMap[this.currentWeather.text] || '🌈'
    },
    weatherType() {
      const typeMap = {
        '晴': 'sunny',
        '多云': 'cloudy',
        '阴': 'cloudy',
        '阵雨': 'rainy',
        '雷阵雨': 'thunder',
        '雨夹雪': 'snowy',
        '小雨': 'rainy',
        '中雨': 'rainy',
        '大雨': 'rainy',
        '暴雨': 'rainy',
        '大暴雨': 'rainy',
        '特大暴雨': 'rainy',
        '阵雪': 'snowy',
        '小雪': 'snowy',
        '中雪': 'snowy',
        '大雪': 'snowy',
        '暴雪': 'snowy',
        '雾': 'cloudy',
        '冻雨': 'snowy'
      }
      return typeMap[this.currentWeather.text] || 'sunny'
    },
    adjustedParticleCount() {
      const baseCounts = {
        sunny: 30,
        rainy: 100,
        cloudy: 0,
        snowy: 80,
        thunder: 5
      }
      return baseCounts[this.weatherType] || 30
    }
  },
  watch: {
    weatherType(newType) {
      if (this.isCanvasReady) {
        this.startEffectByWeather();
      }
    }
  },
  mounted() {
    this.initCanvas();
    this.setupResizeObserver();
    this.handleSocketMessage = this.handleSocketMessage.bind(this);
    this.$store.commit('ADD_MESSAGE_LISTENER', this.handleSocketMessage);
  },
  created() {
    this.fetchWeatherData();

    // 每小时更新一次天气数据
    this.weatherInterval = setInterval(() => {
      this.fetchWeatherData();
    }, 3600000); // 1小时
  },
  beforeDestroy() {
    this.$store.commit('REMOVE_MESSAGE_LISTENER', this.handleSocketMessage);
    // 清除定时器
    if (this.weatherInterval) {
      clearInterval(this.weatherInterval);
    }
    this.cleanUp();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    handleSocketMessage(msg) {
      switch (msg.msgType) {
        case _code.REFRESH_USER_COIN: // 刷新金币
        case _code.RECHARGE_SUCCESS: // 充值成功
          this.$store.dispatch('USERINFO');
          break;
      }
    },
    initCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      this.canvasCtx = canvas.getContext('2d');
      this.isCanvasReady = true;

      this.startEffectByWeather();
    },

    setupResizeObserver() {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          const canvas = this.$refs.canvas;
          if (canvas) {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            this.startEffectByWeather();
          }
        });
        this.resizeObserver.observe(document.body);
      }
    },

    async fetchWeatherData() {
      this.$http.get('/gatherIndex/getweather').then(res => {
        const weatherData = res.data.now;
        console.log('天气数据:', weatherData);
        this.currentWeather = {
          text: weatherData.text,
          temp: weatherData.temp
        };
        this.currentTemp = parseInt(weatherData.temp) || 25;

        if (this.isCanvasReady) {
          this.startEffectByWeather();
        }
      }).catch(err => {
        console.error('获取天气数据失败:', err);
        // 使用默认天气数据
        this.currentWeather = {
          text: '晴',
          temp: '25'
        };
        this.currentTemp = 25;
      });
    },

    startEffectByWeather() {
      this.cleanUp();
      this.effectStartTime = Date.now();

      switch(this.weatherType) {
        case 'sunny':
          this.startSunEffect();
          break;
        case 'rainy':
          this.startRainEffect();
          break;
        case 'cloudy':
          this.startCloudEffect();
          break;
        case 'snowy':
          this.startSnowEffect();
          break;
        case 'thunder':
          this.startThunderEffect();
          break;
        default:
          this.startSunEffect();
      }
    },

    cleanUp() {
      cancelAnimationFrame(this.animationFrame);
      if (this.canvasCtx && this.$refs.canvas) {
        this.canvasCtx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
      }
      this.particles = [];
    },

    startSunEffect() {
      const intensity = 0.7;
      this.particles = Array(this.adjustedParticleCount).fill().map(() => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        size: Math.random() * 3 * intensity + 1,
        speed: Math.random() * 1.5 * intensity + 0.5,
        opacity: Math.random() * 0.4 * intensity + 0.3
      }));
      this.animateParticles('#FFD700', true);
    },

    startRainEffect() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      const intensity = 0.7;

      this.particles = Array(this.adjustedParticleCount).fill().map(() => ({
        x: Math.random() * canvas.width,
        y: Math.random() * -200 * (1 + intensity),
        length: Math.random() * 15 * intensity + 10,
        speed: Math.random() * 8 * intensity + 6,
        opacity: Math.random() * 0.5 * intensity + 0.3
      }));
      this.animateRain();
    },

    startSnowEffect() {
      const intensity = 0.7;

      this.particles = Array(this.adjustedParticleCount).fill().map(() => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * -50,
        size: Math.random() * 3 * intensity + 1,
        speed: Math.random() * 2 * intensity + 0.5,
        swing: (Math.random() * 2 - 1) * intensity,
        opacity: Math.random() * 0.7 * intensity + 0.2
      }));
      this.animateParticles('#FFFFFF', false);
    },

    startCloudEffect() {
      this.cleanUp();
    },

    startThunderEffect() {
      this.cleanUp();
      let flashCount = 0;
      const intensity = 0.8;

      const flash = () => {
        if (flashCount++ > Math.floor(intensity * 5) || !this.$refs.canvas || !this.canvasCtx) {
          if (this.loop) {
            setTimeout(() => this.startThunderEffect(), 5000 + Math.random() * 10000);
          }
          return;
        }

        this.canvasCtx.fillStyle = `rgba(255,255,255,${0.5 * intensity})`;
        this.canvasCtx.fillRect(0, 0, window.innerWidth, window.innerHeight);

        setTimeout(() => {
          this.drawLightning(intensity);
          setTimeout(() => {
            this.canvasCtx.clearRect(0, 0, window.innerWidth, window.innerHeight);
            setTimeout(flash, Math.random() * 2000 * (1 - intensity * 0.5) + 1000);
          }, 100);
        }, 50);
      };
      flash();
    },

    animateParticles(color, isSun) {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.canvasCtx) return;

      const ctx = this.canvasCtx;
      const { width, height } = canvas;

      const animate = () => {
        if (!document.hidden) {
          ctx.clearRect(0, 0, width, height);

          if (this.loop && Date.now() - this.effectStartTime > 15000) {
            this.effectStartTime = Date.now();
            this.startEffectByWeather();
            return;
          }

          this.particles.forEach(p => {
            if (isSun) {
              p.y -= p.speed;
              if (p.y < -10) p.y = height + 10;
            } else {
              p.y += p.speed;
              p.x += p.swing * 0.1;
              if (p.y > height) {
                p.y = Math.random() * -50;
                p.x = Math.random() * width;
              }
            }

            ctx.beginPath();
            ctx.fillStyle = `rgba(${this.hexToRgb(color)}, ${p.opacity})`;
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
          });
        }

        this.animationFrame = requestAnimationFrame(animate);
      };
      animate();
    },

    animateRain() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.canvasCtx) return;

      const ctx = this.canvasCtx;
      const { width, height } = canvas;

      const animate = () => {
        if (!document.hidden) {
          ctx.clearRect(0, 0, width, height);

          if (this.loop && Date.now() - this.effectStartTime > 15000) {
            this.effectStartTime = Date.now();
            this.startEffectByWeather();
            return;
          }

          this.particles.forEach(p => {
            p.y += p.speed;
            if (p.y > height) {
              p.y = Math.random() * -200;
              p.x = Math.random() * width;
            }

            ctx.beginPath();
            ctx.strokeStyle = `rgba(174, 194, 224, ${p.opacity})`;
            ctx.lineWidth = 1;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p.x + 2, p.y + p.length);
            ctx.stroke();
          });
        }

        this.animationFrame = requestAnimationFrame(animate);
      };
      animate();
    },

    drawLightning(intensity = 0.7) {
      if (!this.$refs.canvas || !this.canvasCtx) return;

      const ctx = this.canvasCtx;
      const width = window.innerWidth;
      const startX = Math.random() * width * 0.3 + width * 0.2;

      ctx.strokeStyle = `rgba(255, 255, 100, ${0.7 * intensity})`;
      ctx.lineWidth = 2 * intensity;
      ctx.shadowBlur = 10 * intensity;
      ctx.shadowColor = '#FFFF00';

      let x = startX;
      let y = 0;
      const segments = 12;

      ctx.beginPath();
      ctx.moveTo(x, y);

      for (let i = 1; i < segments; i++) {
        const nextY = (i / segments) * window.innerHeight;
        x += (Math.random() * 60 - 30) * intensity;
        ctx.lineTo(x, nextY);
      }
      ctx.stroke();
    },

    hexToRgb(hex) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `${r}, ${g}, ${b}`;
    }
  }
};
</script>

<style lang="less" scoped>
.weather-effects {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 999;
  overflow: hidden;

  .effect-canvas {
    position: absolute;
    width: 100%;
    height: 100%;
    will-change: transform;
  }

  .css-effect {
    position: absolute;
    width: 100%;
    height: 100%;

    &.sunny {
      background: radial-gradient(circle at 20% 20%, rgba(255,215,0,0.1), transparent 60%);
      animation: sunshine 8s infinite alternate;
    }

    &.cloudy {
      background: linear-gradient(to bottom, rgba(200,200,200,0.2), transparent);
      animation: cloudyMove 120s linear infinite;
    }
  }

  .weather-indicator {
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(0,0,0,0.1);
    color: white;
    padding: 8px 12px;
    border-radius: 20px;
    font-size: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    backdrop-filter: blur(5px);
    //border: 1px solid #FF6B95;

    .weather-icon {
      font-size: 18px;
      margin-bottom: 4px;
    }

    .temp {
      font-weight: bold;
      margin-bottom: 4px;
    }

    .coins {
      font-size: 12px;
      opacity: 0.8;
    }
  }
}

@keyframes sunshine {
  from { opacity: 0.2; }
  to { opacity: 0.4; }
}

@keyframes cloudyMove {
  from { background-position: 0 0; }
  to { background-position: 100% 0; }
}

@media (max-width: 768px) {
  .weather-effects {
    .weather-indicator {
      top: 10px;
      right: 10px;
      font-size: 12px;
      padding: 6px 10px;

      .weather-icon {
        font-size: 16px;
      }

      .coins {
        font-size: 10px;
      }
    }
  }
}
</style>
